<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive, ref } from 'vue';
import { VDateInput } from 'vuetify/labs/components';

import CardComponent from '@/components/ui/CardComponent.vue';
import { toDateOnlyString } from '@/utils/formatters.ts';

import type { ValidationRule } from 'vuetify/framework';

const { t } = useI18n();

const emits = defineEmits(['closeModal']);

const formValid = ref<boolean>(false);

const fileRules: ValidationRule[] = [
  (v: File | null) => !!v || t('validators.mandate.documentFile.required'),
];
const nameRules: ValidationRule[] = [
  (v: string | null) => !!v || t('validators.mandate.documentFileName.required'),
];
const periodRules: ValidationRule[] = [
  (v: Date | null) => !!v || t('components.managementReportUpload.inputs.reportPeriod.required'),
];

const form = reactive({
  name: null as string | null,
  file: null as File | null,
  reportPeriod: null as Date | null,
});

// VDateInput emits a Date. We only ever need the calendar day, so we convert it to a pure
// "YYYY-MM-DD" string here — using local calendar fields, not toISOString() — before it leaves
// this component. That way nothing downstream (FormData/JSON.stringify) can serialize it as a
// UTC datetime and shift the day depending on the browser's timezone.
function submit(): void {
  emits('closeModal', {
    ...form,
    reportPeriod: toDateOnlyString(form.reportPeriod)
  });
}
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        {{ t('components.managementReportUpload.title') }}
      </template>
      <template #item>
        <v-form v-model="formValid" class="my-4">
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="form.file"
                :label="t('components.managementReportUpload.inputs.file.label')"
                :rules="fileRules"
                accept=".pdf"
                append-inner-icon="PhPaperclip"
                clearable
                hide-details="auto"
                prepend-icon=""
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                :label="t('components.managementReportUpload.inputs.name.label')"
                :rules="nameRules"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <VDateInput
                v-model="form.reportPeriod"
                :label="t('components.managementReportUpload.inputs.reportPeriod.label')"
                :rules="periodRules"
                hide-details="auto"
                prepend-icon=""
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template #actions>
        <div class="mt-2 d-flex flex-row justify-space-between ga-4">
          <v-btn
            variant="outlined"
            @click.prevent="emits('closeModal', false)"
          >
            {{ t('common.buttons.cancel') }}
          </v-btn>
          <v-btn
            :disabled="!formValid"
            color="primary"
            type="submit"
            variant="flat"
            @click="submit"
          >
            {{ t('common.buttons.validate') }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>
